// All mock content for the Jellis Enterprise redesign concept.
// This is frontend-only mock data. Replace with API data during backend integration.

import {
  Search, Target, Share2, Layout, PenTool, BarChart3,
  Phone, ClipboardList, Trophy, Users, Wrench, Home,
  Sparkles, Building2, UtensilsCrossed, Store, Waves, Truck,
  ShieldCheck, HeartHandshake, Rocket,
  Handshake, PhoneCall, Megaphone, Wallet, Globe,
  Scale, CreditCard, HeartPulse, Sun, Car, Bug, Hammer, GraduationCap, Landmark, Stethoscope,
} from "lucide-react";

export const company = {
  name: "Jellis Enterprise",
  legal: "Jellis Enterprise LLC",
  tagline: "Digital marketing that actually moves the needle.",
  phone: "(760) 664-2227",
  phoneHref: "tel:17606642227",
  email: "Support@jellisenterprise.com",
  emailHref: "mailto:Support@jellisenterprise.com",
  address: {
    line1: "6834 Cantrell Rd #1326",
    line2: "Little Rock, AR 72207",
    country: "United States",
  },
  bookingUrl: "https://calendly.com/jellis-enterprise/meet-with-a-team-member-to-discuss-your-next-project",
};

export const nav = [
  { label: "Home", path: "/" },
  { label: "Services", path: "/services" },
  { label: "Networks", path: "/networks" },
  { label: "Who We Help", path: "/who-we-help" },
  { label: "About", path: "/about" },
  { label: "Case Studies", path: "/case-studies" },
  { label: "Blog", path: "/blog" },
  { label: "Contact", path: "/contact" },
];

export const hero = {
  badge: "Arkansas' growth-focused marketing partner",
  titleTop: "Grow your business",
  titleAccent: "the smarter way.",
  subtitle:
    "We combine SEO, paid ads, and social strategy into one unified engine — built to bring your local business more calls, more leads, and more revenue. No guesswork, just growth.",
  primaryCta: "Schedule a Free Call",
  secondaryCta: "Explore Services",
  image:
    "https://images.unsplash.com/photo-1557804506-669a67965ba0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHxkaWdpdGFsJTIwbWFya2V0aW5nJTIwdGVhbXxlbnwwfHx8fDE3ODc2MDU5NTd8MA&ixlib=rb-4.1.0&q=85",
  floatStat1: { value: "8×", label: "Avg. growth" },
  floatStat2: { value: "100+", label: "Clients served" },
};

export const partners = [
  "Google Partner",
  "Meta Business Partner",
  "Microsoft Ads",
  "TikTok for Business",
  "Instagram Partner",
  "YouTube Ads",
];

export const stats = [
  { value: 50, suffix: "+", label: "Active team members" },
  { value: 100, suffix: "+", label: "Clients served to date" },
  { value: 8, suffix: "×", label: "Growth over the past year" },
  { value: 100, suffix: "+", label: "Platform features & tools" },
];

export const process = [
  {
    icon: Phone,
    step: "01",
    title: "Schedule a Call",
    desc: "One free, no-obligation call to understand your goals and map out a personalized marketing plan.",
  },
  {
    icon: ClipboardList,
    step: "02",
    title: "Get Your Plan",
    desc: "Our experts walk you through the strategy and help you choose the plan that delivers the most value.",
  },
  {
    icon: Trophy,
    step: "03",
    title: "Win Online",
    desc: "Launch all-in-one campaigns designed to outrank, outreach, and outperform your competitors.",
  },
  {
    icon: Users,
    step: "04",
    title: "Work With The Best",
    desc: "Industry-leading marketers run your campaigns while you focus on doing what you do best.",
  },
];

export const services = [
  {
    slug: "seo",
    icon: Search,
    title: "SEO",
    short: "Rank higher and get found by customers actively searching for what you offer.",
    long: "Superior website optimization and search engine marketing that earns lasting visibility. We handle technical SEO, local map rankings, content, and authority building.",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxzZW8lMjBhbmFseXRpY3N8ZW58MHx8fHwxNzg3NjA1OTg4fDA&ixlib=rb-4.1.0&q=85",
    points: ["Local & national SEO", "Google Business Profile", "Technical audits", "Content strategy"],
  },
  {
    slug: "google-ads",
    icon: Target,
    title: "Google Ads",
    short: "Put your services in front of ready-to-buy customers with high-intent PPC.",
    long: "Direct your target demographic straight to your products and services through powerful PPC campaigns on first-tier search engines, tuned daily for maximum ROI.",
    image:
      "https://images.unsplash.com/photo-1611926653458-09294b3142bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxvbmxpbmUlMjBhZHZlcnRpc2luZ3xlbnwwfHx8fDE3ODc2MDU5ODh8MA&ixlib=rb-4.1.0&q=85",
    points: ["Search & Performance Max", "Call & lead campaigns", "Landing page CRO", "Daily bid optimization"],
  },
  {
    slug: "social-ads",
    icon: Share2,
    title: "Facebook & Social Ads",
    short: "Generate leads and sales within days from precision-targeted social campaigns.",
    long: "Full-funnel Meta, Instagram, and TikTok advertising with scroll-stopping creative and airtight targeting that turns feeds into pipeline.",
    image:
      "https://images.unsplash.com/photo-1683721003111-070bcc053d8b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxzb2NpYWwlMjBtZWRpYSUyMG1hcmtldGluZ3xlbnwwfHx8fDE3ODc2MDU5ODh8MA&ixlib=rb-4.1.0&q=85",
    points: ["Meta & Instagram ads", "TikTok campaigns", "Creative production", "Retargeting funnels"],
  },
  {
    slug: "web-design",
    icon: Layout,
    title: "Web Design & Development",
    short: "Fast, beautiful websites built to convert visitors into customers.",
    long: "Conversion-first websites engineered for speed, mobile, and search. Every page is designed to guide visitors toward booking, calling, or buying.",
    image:
      "https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxvbmxpbmUlMjBhZHZlcnRpc2luZ3xlbnwwfHx8fDE3ODc2MDU5ODh8MA&ixlib=rb-4.1.0&q=85",
    points: ["Conversion-focused UX", "Mobile-first builds", "Blazing performance", "SEO-ready structure"],
  },
  {
    slug: "content",
    icon: PenTool,
    title: "Content Marketing",
    short: "Content that educates, ranks, and earns trust with your audience.",
    long: "Strategic blogs, landing pages, email, and social content that establishes authority and keeps your brand top-of-mind across every channel.",
    image:
      "https://images.unsplash.com/photo-1724862936518-ae7fcfc052c1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxzb2NpYWwlMjBtZWRpYSUyMG1hcmtldGluZ3xlbnwwfHx8fDE3ODc2MDU5ODh8MA&ixlib=rb-4.1.0&q=85",
    points: ["Blog & SEO content", "Email campaigns", "Social content", "Brand storytelling"],
  },
  {
    slug: "analytics",
    icon: BarChart3,
    title: "Analytics & Reporting",
    short: "Crystal-clear reporting so you always know exactly what's working.",
    long: "Transparent dashboards and monthly reviews that tie every dollar to a result. No vanity metrics — just the numbers that grow your business.",
    image:
      "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxzZW8lMjBhbmFseXRpY3N8ZW58MHx8fHwxNzg3NjA1OTg4fDA&ixlib=rb-4.1.0&q=85",
    points: ["Live dashboards", "Call tracking", "ROI attribution", "Monthly strategy reviews"],
  },
];

export const networks = [
  {
    slug: "affiliate-network",
    icon: Handshake,
    tag: "Performance Marketing",
    title: "Affiliate Network & Partnerships",
    short:
      "Recruit, manage, and scale a network of trusted partners who drive real sales — and only pay when they deliver.",
    long:
      "We build and run your affiliate program end to end: recruiting high-quality partners, setting up airtight tracking and payouts, and continuously optimizing offers so your best affiliates keep producing. It's growth on a pure performance basis — you pay for results, not promises.",
    image:
      "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1200&q=80",
    points: ["Partner recruitment & vetting", "Airtight tracking & attribution", "Automated commission payouts", "Fraud & compliance monitoring"],
  },
  {
    slug: "pay-per-call",
    icon: PhoneCall,
    tag: "Inbound Calls",
    title: "Pay Per Call Network",
    short:
      "Connect with publishers who send you live, ready-to-buy phone calls — and pay only for qualified calls.",
    long:
      "Our pay-per-call network routes high-intent inbound calls straight to your business. We handle call tracking, dynamic routing, and quality filters so every call that reaches you is a real opportunity. Perfect for high-ticket and urgent services where a live conversation closes the deal.",
    image:
      "https://images.unsplash.com/photo-1712159018726-4564d92f3ec2?auto=format&fit=crop&w=1200&q=80",
    points: ["Exclusive, qualified inbound calls", "Real-time call tracking & routing", "Per-call performance pricing", "Duplicate & spam filtering"],
  },
];

export const networkProcess = [
  { icon: Megaphone, step: "01", title: "Advertisers set the offer", desc: "Businesses define what a sale or qualified call is worth to them." },
  { icon: Users, step: "02", title: "Partners promote it", desc: "Vetted affiliates and publishers drive targeted traffic and calls to the offer." },
  { icon: BarChart3, step: "03", title: "We track everything", desc: "Every click, lead, and call is tracked, verified, and attributed in real time." },
  { icon: Wallet, step: "04", title: "Everyone gets paid", desc: "Advertisers pay only for results; partners get reliable, on-time payouts." },
];

export const networkAudience = [
  {
    icon: Target,
    title: "For Advertisers & Businesses",
    subtitle: "Grow on a pure pay-for-performance basis.",
    cta: "Advertise / Get Calls",
    points: [
      "Only pay for real results — sales, leads, or qualified calls",
      "Tap into a network of vetted, motivated partners",
      "Full transparency with real-time tracking & reporting",
      "Scale up or down with zero wasted ad spend",
    ],
  },
  {
    icon: Globe,
    title: "For Publishers & Affiliates",
    subtitle: "Monetize your traffic and audience.",
    cta: "Become a Partner",
    points: [
      "Competitive commissions and per-call payouts",
      "High-converting offers across top verticals",
      "Fast, on-time payments you can count on",
      "Dedicated support to help you earn more",
    ],
  },
];

export const networkStats = [
  { value: 500, suffix: "+", label: "Active partners & publishers" },
  { value: 1, suffix: "M+", label: "Calls & leads delivered" },
  { value: 40, suffix: "+", label: "Verticals covered" },
  { value: 98, suffix: "%", label: "On-time payout rate" },
];

export const networkVerticals = [
  {
    icon: Wrench, name: "Home Services", slug: "home-services", desc: "Plumbing, HVAC, electrical & repairs",
    headline: "Exclusive home-service calls, ready to book",
    subhead: "Live, high-intent calls from homeowners who need plumbing, HVAC, roofing, and electrical help — right now.",
    bullets: ["Emergency & scheduled service calls", "Geo-targeted to your service area", "Pay only for qualified calls", "Scale fast during peak season"],
    faqs: [
      { q: "Are these calls exclusive to my business?", a: "Yes. Every call is delivered live and one-to-one — never shared, resold, or recycled to competitors in your service area." },
      { q: "How much do home-service calls cost?", a: "You pay a flat per-call rate only for qualified calls that meet the duration and criteria we agree on up front. Pricing varies by trade and market, with no long-term contract." },
      { q: "What counts as a qualified call?", a: "A homeowner in your service area requesting a service you offer who stays on the line past your agreed connection time. Wrong numbers, spam, and out-of-area calls are never billed." },
      { q: "How fast can calls start coming in?", a: "Most home-service campaigns go live within 3–5 business days once we confirm your service area, hours, and call routing." },
      { q: "Can I control when and how many calls I get?", a: "Absolutely. We route calls only during your business hours and can cap daily volume so your team is never overwhelmed." },
    ],
  },
  {
    icon: Scale, name: "Legal", slug: "legal", desc: "Personal injury, mass tort & more",
    headline: "Signed-case-ready legal calls",
    subhead: "Connect with claimants actively seeking representation for personal injury, mass tort, and more.",
    bullets: ["Personal injury & mass tort", "TCPA-compliant, screened callers", "Practice-area & state targeting", "Pay per qualified call"],
    faqs: [
      { q: "Are callers pre-screened for my practice area?", a: "Yes. We screen callers by case type, incident details, and state before transferring, so you speak with prospects who fit your intake criteria." },
      { q: "Are your legal calls TCPA-compliant?", a: "Yes. All calls are sourced through consented, compliant channels and documented, so your firm stays protected." },
      { q: "How is pricing structured?", a: "You pay a flat rate per qualified, screened call — not per signed case — with rates set by practice area and geography." },
      { q: "Can I target specific case types and states?", a: "Yes. We filter by practice area (personal injury, mass tort, and more) and jurisdiction so every call matches what you handle." },
      { q: "How soon can we start receiving calls?", a: "Most legal campaigns launch within a week after we confirm your intake criteria and call routing." },
    ],
  },
  {
    icon: ShieldCheck, name: "Insurance", slug: "insurance", desc: "Auto, home, life & health",
    headline: "Inbound insurance calls that convert",
    subhead: "Reach shoppers comparing auto, home, life, and health coverage the moment they're ready to buy.",
    bullets: ["Auto, home, life & health lines", "Age & geo filtering", "Real-time call transfers", "Only pay for qualified calls"],
    faqs: [
      { q: "Which insurance lines can you deliver calls for?", a: "Auto, home, life, and health. We match callers to the lines and states you're licensed for." },
      { q: "Are these live transfers or callbacks?", a: "Both are available. Most partners choose real-time warm transfers so you speak with shoppers the moment they're ready to buy." },
      { q: "How does per-call pricing work?", a: "You pay only for calls that meet your agreed length and qualification filters (age, geo, coverage type). No shared or aged leads." },
      { q: "Can I filter by age and location?", a: "Yes. We apply age, geography, and coverage-type filters before any call reaches you." },
      { q: "How fast can I ramp up volume?", a: "Campaigns typically go live in 3–5 business days, and we scale volume as your close rate proves out." },
    ],
  },
  {
    icon: Landmark, name: "Debt Relief", slug: "debt-relief", desc: "Settlement & consolidation",
    headline: "Qualified debt-relief calls",
    subhead: "Speak with consumers seeking debt settlement and consolidation who meet your minimums.",
    bullets: ["Unsecured debt thresholds", "Pre-qualified, screened callers", "State-by-state targeting", "Performance-based pricing"],
    faqs: [
      { q: "Are callers pre-qualified by debt amount?", a: "Yes. We screen for unsecured-debt minimums and any other thresholds you set before transferring the call." },
      { q: "How is pricing determined?", a: "A flat rate per qualified call, based on your debt minimums, target states, and call-duration requirements." },
      { q: "Are the calls compliant?", a: "All calls come from consented, compliant sources with documentation to keep your program protected." },
      { q: "Can I target specific states?", a: "Yes. We deliver calls only from the states where you operate and that meet your qualification criteria." },
      { q: "How exclusive are the calls?", a: "Every call is delivered to you alone — never shared with other debt-relief providers." },
    ],
  },
  {
    icon: HeartPulse, name: "Medicare & Health", slug: "medicare-health", desc: "Medicare, ACA & supplemental",
    headline: "Compliant Medicare & health calls",
    subhead: "Connect with seniors and shoppers exploring Medicare, ACA, and supplemental plans.",
    bullets: ["AEP & OEP-ready volume", "Age-in & eligibility filters", "Compliant lead sourcing", "Live warm transfers"],
    faqs: [
      { q: "Are your Medicare calls compliant?", a: "Yes. We source through compliant, consented channels and screen for eligibility to keep your agency protected." },
      { q: "Can you handle AEP and OEP volume?", a: "Absolutely. We scale volume for Annual and Open Enrollment periods and can cap daily calls to match your team's capacity." },
      { q: "How do you qualify callers?", a: "We filter by age-in, eligibility, and plan interest (Medicare, ACA, supplemental) before any warm transfer." },
      { q: "How does pricing work?", a: "Flat per-qualified-call pricing — you only pay for calls that meet your length and eligibility criteria." },
      { q: "Are these live transfers or leads?", a: "Live warm transfers, so you connect with shoppers while their interest is highest." },
    ],
  },
  {
    icon: Sun, name: "Solar & Energy", slug: "solar-energy", desc: "Residential solar & savings",
    headline: "Homeowner solar calls, pre-screened",
    subhead: "Reach homeowners interested in cutting energy bills with residential solar.",
    bullets: ["Homeowner & credit filters", "Roof & utility qualification", "Geo & incentive targeting", "Pay per qualified call"],
    faqs: [
      { q: "Are homeowners pre-screened?", a: "Yes. We verify homeownership and can apply credit, roof, and utility filters before delivering the call." },
      { q: "How is pricing set?", a: "A flat rate per qualified call based on your target market, filters, and call-duration requirements." },
      { q: "Can I target my service area?", a: "Yes. Calls are geo-targeted to the regions and utility territories you serve." },
      { q: "Are the calls exclusive?", a: "Every solar call is delivered to you alone — never shared with competing installers." },
      { q: "How soon can calls start?", a: "Most solar campaigns launch within 3–5 business days of confirming your criteria." },
    ],
  },
  {
    icon: Hammer, name: "Home Improvement", slug: "home-improvement", desc: "Roofing, windows & remodeling",
    headline: "Ready-to-quote remodeling calls",
    subhead: "Connect with homeowners planning roofing, windows, baths, and remodeling projects.",
    bullets: ["Project-type targeting", "Homeowner verification", "Local service-area match", "Only pay for real opportunities"],
    faqs: [
      { q: "Can I target specific project types?", a: "Yes — roofing, windows, baths, kitchens, and more. You choose exactly which projects you want calls for." },
      { q: "Are homeowners verified?", a: "We verify homeownership and project intent before delivering the call to your team." },
      { q: "How does pricing work?", a: "Flat per-qualified-call pricing set by project type and market — with no long-term contracts." },
      { q: "Are the calls exclusive to me?", a: "Yes. Every call is one-to-one and matched to your local service area." },
      { q: "How quickly can I get started?", a: "Campaigns typically go live within 3–5 business days." },
    ],
  },
  {
    icon: Car, name: "Auto", slug: "auto", desc: "Insurance, warranty & sales",
    headline: "High-intent auto calls",
    subhead: "Reach drivers shopping for insurance, warranties, and vehicles who are ready to talk.",
    bullets: ["Insurance, warranty & sales", "Vehicle & coverage filters", "Geo targeting", "Per-call performance pricing"],
    faqs: [
      { q: "What kinds of auto calls do you offer?", a: "Insurance, extended warranty, and vehicle-sales calls, matched to the products you sell." },
      { q: "How are callers qualified?", a: "We filter by vehicle, coverage interest, and geography before connecting the call to you." },
      { q: "How does per-call pricing work?", a: "You pay only for calls that meet your agreed duration and qualification filters." },
      { q: "Can I target specific regions?", a: "Yes. All calls are geo-targeted to your licensed or service areas." },
      { q: "Are the calls shared with others?", a: "No. Every auto call is delivered exclusively to you." },
    ],
  },
  {
    icon: CreditCard, name: "Financial Services", slug: "financial-services", desc: "Loans, credit & tax relief",
    headline: "Financial-services calls that qualify",
    subhead: "Speak with consumers seeking loans, credit help, and tax relief who meet your criteria.",
    bullets: ["Loans, credit & tax relief", "Income & eligibility filters", "Compliant lead sourcing", "Pay per qualified call"],
    faqs: [
      { q: "Which financial products can you deliver calls for?", a: "Personal loans, credit repair, and tax relief, matched to your offerings and licensing." },
      { q: "Are callers pre-qualified?", a: "Yes. We screen by income, eligibility, and product interest before transferring." },
      { q: "Are the calls compliant?", a: "All calls are sourced through consented, compliant channels with documentation." },
      { q: "How is pricing structured?", a: "A flat rate per qualified call, based on your criteria and target states." },
      { q: "How fast can we launch?", a: "Most campaigns start within a week once criteria and routing are confirmed." },
    ],
  },
  {
    icon: Stethoscope, name: "Addiction & Rehab", slug: "addiction-rehab", desc: "Treatment & recovery admissions",
    headline: "Compassionate, compliant admissions calls",
    subhead: "Connect with individuals and families seeking treatment and recovery options.",
    bullets: ["Insurance-verified callers", "Ethical, compliant sourcing", "Facility & location match", "Live warm transfers"],
    faqs: [
      { q: "Are your admissions calls ethically sourced?", a: "Yes. We use compliant, consented, LegitScript-conscious sourcing — no deceptive practices, ever." },
      { q: "Are callers insurance-verified?", a: "We can verify insurance and screen for facility fit before a warm transfer." },
      { q: "How does pricing work?", a: "Flat per-qualified-call pricing based on your criteria — you never pay for calls that don't fit." },
      { q: "Can I match calls to my facility and location?", a: "Yes. We route by location, program, and level of care you provide." },
      { q: "Are these live transfers?", a: "Yes — compassionate live warm transfers so families reach you at the right moment." },
    ],
  },
  {
    icon: Bug, name: "Pest Control", slug: "pest-control", desc: "Residential & commercial",
    headline: "Local pest-control calls on demand",
    subhead: "Reach residential and commercial customers who need service scheduled fast.",
    bullets: ["Residential & commercial", "Service-type targeting", "Local geo match", "Pay only for qualified calls"],
    faqs: [
      { q: "Residential, commercial, or both?", a: "Both. You choose the customer types and service categories you want calls for." },
      { q: "Are calls local to my area?", a: "Yes. Every call is geo-matched to your service territory." },
      { q: "How is pricing set?", a: "Flat per-qualified-call pricing — you only pay for real service opportunities." },
      { q: "Are the calls exclusive?", a: "Yes. Calls are delivered one-to-one and never shared with other pest-control companies." },
      { q: "How fast can calls start?", a: "Most pest-control campaigns go live within 3–5 business days." },
    ],
  },
  {
    icon: GraduationCap, name: "Education", slug: "education", desc: "Higher ed & career training",
    headline: "Enrollment-ready education calls",
    subhead: "Connect with prospective students exploring higher-ed and career-training programs.",
    bullets: ["Program & degree targeting", "Pre-screened prospects", "Compliant lead sourcing", "Per-call pricing"],
    faqs: [
      { q: "Can I target specific programs?", a: "Yes — degrees, certificates, and career-training programs. You pick the fields you enroll for." },
      { q: "Are prospects pre-screened?", a: "We screen for program interest and basic eligibility before delivering the call." },
      { q: "Are the calls compliant?", a: "All calls come from consented, compliant sources with documentation." },
      { q: "How does pricing work?", a: "A flat rate per qualified call, based on program type and target regions." },
      { q: "Are calls exclusive to my institution?", a: "Yes. Every enrollment call is delivered to you alone." },
    ],
  },
];

export const whyChoose = [
  {
    icon: ShieldCheck,
    title: "Industry Leaders",
    desc: "Our team shares over 25 years of combined marketing experience. We know how to overcome any hurdle, in any industry.",
  },
  {
    icon: HeartHandshake,
    title: "Quality Guaranteed",
    desc: "We're only as good as our most recent win. From mom-and-pop shops to Fortune 500s, we obsess over outstanding results.",
  },
  {
    icon: Rocket,
    title: "Ongoing Support",
    desc: "When you work with us, you're family. Our experts are on standby daily — and you always get the final say on your campaign.",
  },
];

export const industries = [
  {
    icon: Wrench,
    title: "Plumbing",
    desc: "Fill the calendar with high-value service calls and emergency jobs.",
    image:
      "https://images.unsplash.com/photo-1749532125405-70950966b0e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxwbHVtYmVyfGVufDB8fHx8MTc4NzYwNjAyM3ww&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: Waves,
    title: "Pool Builders",
    desc: "Book premium installs with targeted seasonal ad campaigns.",
    image:
      "https://images.pexels.com/photos/6196685/pexels-photo-6196685.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    icon: Home,
    title: "Home Services",
    desc: "HVAC, roofing, cleaning & contractors — become the local go-to.",
    image:
      "https://images.unsplash.com/photo-1676311396794-f14881e9daaa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxob21lJTIwc2VydmljZXN8ZW58MHx8fHwxNzg3NjA2MDIzfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: UtensilsCrossed,
    title: "Restaurants",
    desc: "Drive reservations, reviews, and repeat local diners.",
    image:
      "https://images.unsplash.com/photo-1594402919317-9e67dca0a305?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxsb2NhbCUyMGJ1c2luZXNzfGVufDB8fHx8MTc4NzYwNjAyM3ww&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: Store,
    title: "Local Retail",
    desc: "Turn foot traffic and online searches into loyal customers.",
    image:
      "https://images.unsplash.com/photo-1571204829887-3b8d69e4094d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxsb2NhbCUyMGJ1c2luZXNzfGVufDB8fHx8MTc4NzYwNjAyM3ww&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: Building2,
    title: "SMBs & Entrepreneurs",
    desc: "Scalable strategies for growing teams and ambitious founders.",
    image:
      "https://images.pexels.com/photos/6476257/pexels-photo-6476257.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export const caseStudies = [
  {
    client: "Riverside Plumbing Co.",
    industry: "Plumbing",
    summary: "Local SEO + Google Ads overhaul that flooded the schedule with high-ticket jobs.",
    image:
      "https://images.unsplash.com/photo-1749532125405-70950966b0e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxwbHVtYmVyfGVufDB8fHx8MTc4NzYwNjAyM3ww&ixlib=rb-4.1.0&q=85",
    metrics: [
      { value: "312%", label: "More leads" },
      { value: "4.2×", label: "Return on ad spend" },
      { value: "#1", label: "Map ranking" },
    ],
  },
  {
    client: "BlueWave Pools",
    industry: "Pool Builders",
    summary: "Seasonal social campaigns that booked out the entire summer install calendar.",
    image:
      "https://images.pexels.com/photos/6196685/pexels-photo-6196685.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    metrics: [
      { value: "$1.4M", label: "Pipeline generated" },
      { value: "68", label: "Premium installs" },
      { value: "5.1×", label: "ROAS" },
    ],
  },
  {
    client: "Cantrell HVAC",
    industry: "Home Services",
    summary: "Full-funnel rebuild — new site, SEO, and PPC — that doubled service revenue.",
    image:
      "https://images.unsplash.com/photo-1676311396794-f14881e9daaa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxob21lJTIwc2VydmljZXN8ZW58MHx8fHwxNzg3NjA2MDIzfDA&ixlib=rb-4.1.0&q=85",
    metrics: [
      { value: "2.1×", label: "Revenue growth" },
      { value: "-38%", label: "Cost per lead" },
      { value: "14k", label: "Monthly visitors" },
    ],
  },
  {
    client: "Maple & Main Bistro",
    industry: "Restaurants",
    summary: "Review generation and local social that packed the dining room every weekend.",
    image:
      "https://images.unsplash.com/photo-1594402919317-9e67dca0a305?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxsb2NhbCUyMGJ1c2luZXNzfGVufDB8fHx8MTc4NzYwNjAyM3ww&ixlib=rb-4.1.0&q=85",
    metrics: [
      { value: "+540", label: "5-star reviews" },
      { value: "89%", label: "Weekend occupancy" },
      { value: "3.6×", label: "Social reach" },
    ],
  },
];

export const testimonials = [
  {
    quote:
      "Quality ad creatives and high-performing campaigns every single time. I genuinely can't recommend them enough.",
    name: "Charlene Griffin",
    role: "Owner, Griffin Home Services",
    initials: "CG",
  },
  {
    quote:
      "Within 60 days our phones were ringing off the hook. The team treats our budget like it's their own money.",
    name: "Marcus DeLeon",
    role: "GM, Riverside Plumbing Co.",
    initials: "MD",
  },
  {
    quote:
      "Finally an agency that reports in plain English and actually delivers ROI. Our best year yet, by far.",
    name: "Priya Sharma",
    role: "Founder, BlueWave Pools",
    initials: "PS",
  },
];

export const values = [
  { icon: Sparkles, title: "One Unified Team", desc: "Every service under one roof so your strategy actually connects." },
  { icon: Target, title: "Results Over Vanity", desc: "We optimize for revenue and leads — not likes and impressions." },
  { icon: HeartHandshake, title: "Radical Transparency", desc: "Clear reporting and honest advice, every step of the way." },
  { icon: Rocket, title: "Built to Scale", desc: "Strategies that grow right alongside your business." },
];

export const team = [
  { name: "Jordan Ellis", role: "Founder & CEO", initials: "JE" },
  { name: "Dana Whitfield", role: "Head of Paid Media", initials: "DW" },
  { name: "Alex Rivera", role: "Director of SEO", initials: "AR" },
  { name: "Sam Carter", role: "Creative Lead", initials: "SC" },
];

export const faqs = [
  {
    q: "How quickly will I see results?",
    a: "Paid campaigns can generate leads within days, while SEO typically builds momentum over 3–6 months. We set clear expectations upfront and report progress every month.",
  },
  {
    q: "Do I have to sign a long-term contract?",
    a: "No. We earn your business month to month. Our goal is to make the results so good you never want to leave.",
  },
  {
    q: "What size businesses do you work with?",
    a: "Everyone from solo entrepreneurs and local home-service pros to established multi-location companies and Fortune 500 brands.",
  },
  {
    q: "Is the first call really free?",
    a: "Absolutely. It's a no-obligation strategy call to understand your goals and see if we're the right fit — no pressure, no strings.",
  },
];
